export const BUSINESS_AMERICAS_START_DATE = "2027-02-15";
export const GOL_PREMIUM_LABEL = "GOL Premium";
export const API_DATA_FLAG = "api-data";
export const NOVA_LOPA_WEB_FLAG = "ENABLE_NOVA_LOPA_SMLPRJI740";
export const LOCAL_NOVA_LOPA_KEY = "vbzNovaLopa";

const PREMIUM_FARE_NAMES = [
  "tarifa premium voebiz",
  "premium economy",
  "premium",
  "economica premium",
  "business americas",
  "business america",
];

type ApiDataFlag = {
  constants?: {
    WEB?: Record<string, unknown>;
  };
  value?: {
    constants?: {
      WEB?: Record<string, unknown>;
    };
  };
};

// eslint-disable-next-line no-unused-vars
type GetConstants = (flagName: string) => unknown;

type CoreHelpers = {
  getConstants?: GetConstants;
};

const CORE_CONTEXT_KEY = "__CoreContext";

const isMissingCoreConstant = (value: unknown): boolean => {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(value, "message");
};

const readNovaLopaFromCore = (): boolean | null => {
  const browserWindow = window as unknown as Record<string, { Helpers?: CoreHelpers } | undefined>;
  const helpers = browserWindow[CORE_CONTEXT_KEY]?.Helpers;
  if (!helpers || typeof helpers.getConstants !== "function") {
    return null;
  }

  try {
    const value = helpers.getConstants(NOVA_LOPA_WEB_FLAG);
    if (value === true || value === false) {
      return value;
    }
    if (isMissingCoreConstant(value)) {
      return false;
    }
    return null;
  } catch {
    return null;
  }
};

const readNovaLopaFromFlags = (flags?: Record<string, unknown> | null): boolean => {
  const apiData = flags?.[API_DATA_FLAG] as ApiDataFlag | undefined;
  const webConstants = apiData?.constants?.WEB ?? apiData?.value?.constants?.WEB;
  return webConstants?.[NOVA_LOPA_WEB_FLAG] === true;
};

const readNovaLopaFromLocalDebug = (): boolean | null => {
  if (window.location.hostname !== "localhost") {
    return null;
  }

  const value = window.localStorage.getItem(LOCAL_NOVA_LOPA_KEY);
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
};

export const isGolPremiumEnabled = (flags?: Record<string, unknown> | null): boolean => {
  const fromCore = readNovaLopaFromCore();
  if (fromCore !== null) {
    return fromCore;
  }

  const fromLocalDebug = readNovaLopaFromLocalDebug();
  if (fromLocalDebug !== null) {
    return fromLocalDebug;
  }

  return readNovaLopaFromFlags(flags);
};

const normalizeFareName = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const toDateOrder = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoDate) {
    return Number(isoDate[1]) * 10000 + Number(isoDate[2]) * 100 + Number(isoDate[3]);
  }

  const usDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(value);
  if (usDate) {
    return Number(usDate[3]) * 10000 + Number(usDate[1]) * 100 + Number(usDate[2]);
  }

  return null;
};

const isPremiumFare = (description: string): boolean =>
  PREMIUM_FARE_NAMES.indexOf(normalizeFareName(description)) !== -1;

const isOnVigencia = (flightDate?: string): boolean => {
  const flightOrder = toDateOrder(flightDate);
  const startOrder = toDateOrder(BUSINESS_AMERICAS_START_DATE);
  return flightOrder !== null && startOrder !== null && flightOrder >= startOrder;
};

export const getStatementFareLabel = (
  description?: string,
  flightDate?: string,
  golPremiumEnabled = false,
): string | undefined => {
  if (!description) {
    return description;
  }

  if (golPremiumEnabled && isPremiumFare(description) && isOnVigencia(flightDate)) {
    return GOL_PREMIUM_LABEL;
  }

  return description;
};

export const getTransactionFlightDate = (
  transaction?: {
    "Flight Date"?: string;
    "Internal Flight Date"?: string;
  } | null,
): string => transaction?.["Flight Date"] || transaction?.["Internal Flight Date"] || "";
